// GEL BÊTA le 07/10/2026. Voir docs/GEL_BETA.md.
// Architecture figée : paiement dual, Coco via settings, rôles user_roles/has_role, âge 18 soft, pas de Connect.
// Porte ouverte : corrections de retours testeurs uniquement sur ordre Hiti, lot unique, sans rouvrir ces règles.
// Interdit sans Hiti : tama-wip→main, édition client.ts / types.ts / .env / config.toml / previewAuthStorage.
import { supabase } from "@/integrations/supabase/client";

/**
 * Palier HLS demandé au premier chargement du lecteur.
 * Valeur métier lue dans `app_settings` (clé ci-dessous), jamais en dur.
 */
export type StartQuality = "lowest" | "auto" | "360" | "480" | "720" | "1080";

export const START_QUALITY_SETTING_KEY = "video_playback_start_quality";

/** Défaut : démarrage le plus rapide possible (réseau 4G polynésien). */
const DEFAULT_START_QUALITY: StartQuality = "lowest";

const ALLOWED: StartQuality[] = ["lowest", "auto", "360", "480", "720", "1080"];

let current: StartQuality = DEFAULT_START_QUALITY;

/** Normalise une valeur inconnue (BDD, saisie) vers un palier autorisé. */
export function parseStartQuality(value: unknown): StartQuality {
  if (typeof value === "string" && (ALLOWED as string[]).includes(value)) {
    return value as StartQuality;
  }
  return DEFAULT_START_QUALITY;
}

export function getStartQuality(): StartQuality {
  return current;
}

export function setStartQuality(quality: StartQuality): void {
  current = parseStartQuality(quality);
}

let startQualityPromise: Promise<StartQuality> | null = null;
let startQualityLoaded = false;

/**
 * Charge le réglage depuis la base, une seule fois par session.
 * Les appels concurrents partagent la même promesse ; une fois terminée
 * (succès ou échec), la valeur en mémoire est renvoyée immédiatement.
 * Best-effort : en cas d'échec on garde le défaut.
 */
export function loadStartQuality(): Promise<StartQuality> {
  if (startQualityLoaded) return Promise.resolve(current);
  if (startQualityPromise) return startQualityPromise;

  startQualityPromise = (async () => {
    try {
      const { data, error } = await supabase
        .from("app_settings")
        .select("value")
        .eq("key", START_QUALITY_SETTING_KEY)
        .maybeSingle();
      if (error) throw error;
      const value = data?.value as { start_quality?: unknown } | null;
      current = parseStartQuality(value?.start_quality);
    } catch (err) {
      console.warn("[playbackQuality] lecture du palier impossible", err);
      current = DEFAULT_START_QUALITY;
    } finally {
      startQualityLoaded = true;
      startQualityPromise = null;
    }
    return current;
  })();
  return startQualityPromise;
}

/** Hauteur de rendu visée, ou null si le palier n'est pas une hauteur fixe. */
export function targetHeightOf(quality: StartQuality = current): number | null {
  const height = Number(quality);
  return Number.isFinite(height) ? height : null;
}
